/**
 * Pomôcky k hľadaniu zákazníka.
 *
 * Farby vlákien počíta frontend, nie server — zoznam farieb je jeden a ten istý
 * pre kazetu, dokumentáciu aj hľadanie, takže sa nemôžu rozísť. Server posiela
 * len čísla (výstup, vstupné vlákno, buffer).
 */
import { FIBER_COLORS } from './fiberColors';
import { NajdenyZakaznik } from '../services/searchService';

export interface FarbaVlakna {
  nazov: string;
  hex: string;
  /** farba textu, aby bol čitateľný na bielej aj čiernej */
  text: string;
}

/**
 * Farba vlákna/výstupu podľa poradového čísla (1 = modrá).
 * Rovnaké priradenie, aké vidí technik v kazete.
 */
export const farbaVlakna = (cislo?: number | null): FarbaVlakna | null => {
  if (!cislo || cislo < 1) return null;
  const f = FIBER_COLORS[(cislo - 1) % FIBER_COLORS.length];
  // Biela a žltá na bielom pozadí nie sú vidieť — text dostane tmavú
  const svetle = ['Biela', 'Žltá', 'Zelená', 'Tyrkysová', 'Ružová'];
  return {
    nazov: f.name,
    hex: f.color,
    text: svetle.includes(f.name) ? '#1a1a1a' : '#ffffff',
  };
};

/** Krátky popis, kde zákazník visí — na jeden riadok do zoznamu. */
export const kdeVisi = (z: NajdenyZakaznik): string => {
  const casti: string[] = [];
  if (z.stlpik?.nazov) casti.push(z.stlpik.nazov);
  if (z.splitter?.index !== null && z.splitter?.index !== undefined) {
    casti.push(`splitter ${z.splitter.index + 1}${z.splitter.typ ? ` (${z.splitter.typ})` : ''}`);
  }
  if (z.vystup) casti.push(`výstup ${z.vystup}`);
  return casti.join(' · ') || 'miesto neznáme';
};

export const popisZdroja = (zdroj: NajdenyZakaznik['zdroj']): string => {
  switch (zdroj) {
    case 'kazeta':
      return 'Zapísal technik do kazety';
    case 'onu':
      return 'Zo zariadenia ONU';
    default:
      return 'Len v zozname zákazníkov';
  }
};

/**
 * Skok z výsledku hľadania na stĺpik v mape.
 *
 * Hľadá sa naprieč projektami, takže výsledok môže byť v inom projekte, než
 * ktorý je práve otvorený — vtedy sa najprv prepne projekt. Stĺpy sa dotiahnu
 * až potom, preto sa požiadavka „otvor kazetu" odloží do poleStore a mapa ju
 * vykoná, keď má stĺpy načítané.
 */
export const prejdiNaZakaznika = async (
  z: NajdenyZakaznik,
  moznosti: { otvorKazetu?: boolean } = {},
): Promise<void> => {
  const { useProjectStore, usePoleStore } = await import('../stores');
  const { useViewStore } = await import('../store/viewStore');

  const projekty = useProjectStore.getState();
  if (projekty.currentProject?.id !== z.projekt.id) {
    const znamy = projekty.projects.find((p) => p.id === z.projekt.id);
    if (znamy) {
      projekty.setCurrentProject(znamy);
    } else {
      await projekty.fetchProject(z.projekt.id);
    }
  }

  const stlpy = usePoleStore.getState();
  if (z.stlpik?.latitude != null && z.stlpik?.longitude != null) {
    stlpy.setZoomTarget({ lat: z.stlpik.latitude, lng: z.stlpik.longitude, zoom: 19 });
  }
  stlpy.setOtvorKazetuPreStlp(moznosti.otvorKazetu && z.stlpik?.id ? z.stlpik.id : null);

  useViewStore.getState().setViewMode('map');
};
